<template>
  <div class="pricing-page">
    <!-- 顶部导航 -->
    <Headers />

    <!-- 页面标题 -->
    <section class="page-hero">
      <div class="hero-container">
        <h1>定价方案</h1>
        <p>透明合理的定价，按需选择最适合的GPU配置</p>

        <!-- 计费模式切换 -->
        <div class="billing-toggle">
          <button class=" main-btn main-btn-small sm2" :class="{ active: billingMode === 'hourly' }" @click="billingMode = 'hourly'">
            <a>
              <i class="el-icon-time"></i>
              <span>按量计费</span>
            </a>
          </button>
          <button class=" main-btn main-btn-small sm2" :class="{ active: billingMode === 'monthly' }" @click="billingMode = 'monthly'">
            <a>
              <i class="el-icon-date"></i>
              <span>包年包月</span>
            </a>
          </button>
        </div>
      </div>
    </section>

    <!-- 高效套餐 -->
    <section class="package-section">
      <div class="package-container">
        <h2>高效套餐 - 即租即用</h2>
        <p class="package-subtitle">算力租赁，秒级启动，按需收费</p>

        <div class="package-features">
          <div class="feature-grid">
            <div class="feature-card">
              <div class="feature-icon ">
                <i class="el-icon-lightning xc-icon"></i>
              </div>
              <h3>秒级启动</h3>
              <p>无需等待，即开即用</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon ">
                <i class="el-icon-coin xc-icon"></i>
              </div>
              <h3>按需收费</h3>
              <p>用多少付多少，无资源浪费</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon ">
                <i class="el-icon-setting xc-icon"></i>
              </div>
              <h3>环境自定义</h3>
              <p>支持私有镜像上传</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon ">
                <i class="el-icon-unlock xc-icon"></i> 
              </div>
              <h3>数据安全</h3>
              <p>物理隔离，私有空间</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 成本对比 -->
    <section class="cost-comparison">
      <div class="cost-container">
        <h2>成本优势显著</h2>
        <div class="cost-grid">
          <div class="cost-card">
            <h3>训练成本对比</h3>
            <div class="cost-chart">
              <div class="cost-bar">
                <div class="bar-label">AI算力云 (RTX 4090)</div>
                <div class="bar-container">
                  <div class="bar-fill our-price" style="width: 45%"></div>
                  <span class="bar-value">￥1.82/小时</span>
                </div>
              </div>
              <div class="cost-bar">
                <div class="bar-label">阿里云 (同等配置)</div>
                <div class="bar-container">
                  <div class="bar-fill competitor" style="width: 85%"></div>
                  <span class="bar-value">￥3.42/小时</span>
                </div>
              </div>
              <div class="cost-bar">
                <div class="bar-label">腾讯云 (同等配置)</div>
                <div class="bar-container">
                  <div class="bar-fill competitor" style="width: 100%"></div>
                  <span class="bar-value">￥4.05/小时</span>
                </div>
              </div>
            </div>
            <div class="cost-savings">
              <span class="savings-text">相比竞品节省成本</span>
              <span class="savings-amount">55%</span>
            </div>
          </div>

          <div class="cost-card">
            <h3>性能表现</h3>
            <div class="performance-metrics">
              <div class="metric-item">
                <div class="metric-number">12.36秒</div>
                <div class="metric-label">Stable Diffusion出图</div>
                <div class="metric-compare">vs 行业平均17.38秒</div>
              </div>
              <div class="metric-item">
                <div class="metric-number">99.9%高可用性</div>
                <div class="metric-label">稳定可靠</div>
                <div class="metric-compare">不间断服务保障</div>
              </div>
              <div class="metric-item">
                <div class="metric-number">40%</div>
                <div class="metric-label">模型加速提升</div>
                <div class="metric-compare">零配置优化</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 场景案例 -->
    <section class="use-cases">
      <div class="cases-container">
        <h2>真实场景成本测算</h2>
        <div class="cases-grid">
          <div class="case-card">
            <div class="case-header">
              <i class="el-icon-reading xc-icon"></i>
              <h3>高校AI实验室</h3>
            </div>
            <div class="case-content">
              <div class="case-scenario">
                <span class="scenario-label">使用场景：</span>
                <span>50名学生并发使用 Python 深度学习</span>
              </div>
              <div class="case-config">
                <span class="config-label">推荐配置：</span>
                <span>RTX 3090 × 5台</span>
              </div>
              <div class="case-cost">
                <div class="cost-breakdown">
                  <div class="cost-item">
                    <span>传统机房建设：</span>
                    <span class="old-price">￥32,000</span>
                  </div>
                  <div class="cost-item">
                    <span>云端解决方案：</span>
                    <span class="new-price">￥10,200</span>
                  </div>
                </div>
                <div class="cost-savings">
                  <span>节省成本：68%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="case-card">
            <div class="case-header">
              <i class="el-icon-picture xc-icon"></i>
              <h3>设计工作室</h3>
            </div>
            <div class="case-content">
              <div class="case-scenario">
                <span class="scenario-label">使用场景：</span>
                <span>AI绘画批量创作，日均500张</span>
              </div>
              <div class="case-config">
                <span class="config-label">推荐配置：</span>
                <span>RTX 4090 × 2台</span>
              </div>
              <div class="case-cost">
                <div class="cost-breakdown">
                  <div class="cost-item">
                    <span>本地渲染成本：</span>
                    <span class="old-price">￥1.2/张</span>
                  </div>
                  <div class="cost-item">
                    <span>云端渲染成本：</span>
                    <span class="new-price">￥0.3/张</span>
                  </div>
                </div>
                <div class="cost-savings">
                  <span>单张成本降低：75%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA区域 -->
    <section class="cta-section">
      <div class="cta-container">
        <h2>开始您的AI算力之旅</h2>
        <p>选择合适的配置，立即享受高性价比的AI算力服务</p>
        <div class="cta-actions">
          <button class="main-btn main-btn-small sm2" @click="startTrial">
            <a>
              <i class="el-icon-rocket"></i>
              免费试用
            </a>
          </button>
          <button class="main-btn main-btn-small sm2" @click="contactSales">
            <a>
              <i class="el-icon-service"></i>
              联系销售
            </a>
          </button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Headers from '@/components/headers.vue'
import Footer from '@/components/footer.vue'
export default {
  name: 'OfficialPricing',
  components: {
    Headers,
    Footer
  },
  data() {
    return {
      billingMode: 'hourly' // 'hourly' or 'monthly'
    }
  },
  methods: {
    selectGPU(gpuType) {
      this.$message.success(`已选择 ${gpuType.toUpperCase()}，即将跳转到配置页面`)
      // 可以跳转到具体的配置页面
      window.open('/login', '_blank')
    },
    startTrial() {
      this.$message.success('免费试用申请已提交，我们将在24小时内联系您')
    },
    contactSales() {
      this.$message.info('请拨打 400-1234-5678 联系销售团队')
    }
  }
}
</script>

<style lang="scss" scoped>
.pricing-page {
  min-height: 100vh;
  background: #0a0e1a;
  color: #ffffff;
}


.page-hero {
  padding: 140px 20px 80px;
  background: radial-gradient(ellipse at center, rgba(102, 126, 234, 0.15) 0%, rgba(10, 14, 26, 1) 70%);
  text-align: center;

  .hero-container {
    max-width: 800px;
    margin: 0 auto;

    h1 {
      font-size: 48px;
      font-weight: 700;
      margin: 0 0 24px;
      background: linear-gradient(135deg, #fff, #fff);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    p {
      font-size: 20px;
      color: #fff;
      margin: 0 0 40px;
    }
  }

  .billing-toggle {
    display: flex;
    justify-content: center;
    gap: 20px;

    .toggle-option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 25px;
      cursor: pointer;
      transition: all 0.3s ease;
      font-weight: 600;

      &:hover {
        border-color: rgba(102, 126, 234, 0.5);
      }

      &.active {
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-color: transparent;
      }

      i {
        font-size: 16px;
      }
    }
  }
}



.package-section {
  padding: 100px 20px;
  background: #0d0d12;

  .package-container {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
  }

  h2 {
    font-size: 36px;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 16px;
  }

  .package-subtitle {
    font-size: 18px;
    color: #fff;
    margin: 0 0 60px;
  }

  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 32px;
  }

  .feature-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 32px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.08);
    }


    .feature-icon {
      width: 64px;
      height: 64px;
      // background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;

      i {
        font-size: 64px;
        color: white;
      }
    }

    h3 {
      font-size: 20px;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 12px;
    }

    p {
      color: #fff;
      margin: 0;
      line-height: 1.5;
    }
  }
}

.cost-comparison {
  padding: 100px 20px;
  background: #000;

  .cost-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  h2 {
    font-size: 36px;
    font-weight: 700;
    text-align: center;
    margin: 0 0 60px;
    background: linear-gradient(135deg, #fff, #fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .cost-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
  }

  .cost-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 32px;

    h3 {
      font-size: 24px;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 24px;
      text-align: center;
    }
  }

  .cost-chart {
    margin-bottom: 20px;

    .cost-bar {
      margin-bottom: 20px;

      .bar-label {
        font-size: 14px;
        color: #fff;
        margin-bottom: 8px;
      }

      .bar-container {
        position: relative;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        height: 32px;
        overflow: hidden;

        .bar-fill {
          height: 100%;
          border-radius: 8px;

          &.our-price {
            background: linear-gradient(135deg, #e1ea66, #f7ca01); // 炫彩渐变
          }

          &.competitor {
            background: rgba(255, 255, 255, 0.3);
          }
        }

        .bar-value {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
        }
      }
    }
  }

  .cost-savings {
    text-align: center;
    padding-top: 60px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    .savings-text {
      color: #fff;
      margin-right: 12px;
    }

    .savings-amount {
      font-size: 24px;
      font-weight: 700;
      color: #52c41a;
    }
  }

  .performance-metrics {
    .metric-item {
      text-align: center;
      margin-bottom: 24px;
      padding-bottom: 20px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);

      &:last-child {
        border-bottom: none;
        margin-bottom: 0;
      }

      .metric-number {
        font-size: 24px;
        font-weight: 700;
        color: #52c41a;
        margin-bottom: 8px;
      }

      .metric-label {
        font-size: 16px;
        color: #ffffff;
        margin-bottom: 4px;
      }

      .metric-compare {
        font-size: 12px;
        color: #fff;
      }
    }
  }
}

.use-cases {
  padding: 100px 20px;
  background: #0d0d12;

  .cases-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  h2 {
    font-size: 36px;
    font-weight: 700;
    text-align: center;
    margin: 0 0 60px;
    color: #ffffff;
  }

  .cases-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  .case-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 32px;

    .case-header {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 24px;

      i {
        font-size: 32px;
        color: #667eea;
      }

      h3 {
        font-size: 24px;
        font-weight: 700;
        color: #ffffff;
        margin: 0;
      }
    }

    .case-content {
      .case-scenario, .case-config {
        margin-bottom: 16px;

        .scenario-label, .config-label {
          color: #8892b0;
          font-weight: 600;
          margin-right: 8px;
        }

        span:last-child {
          color: #ffffff;
        }
      }

      .case-cost {
        .cost-breakdown {
          margin-bottom: 16px;

          .cost-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;

            .old-price {
              color: #ff7875;
              text-decoration: line-through;
            }

            .new-price {
              color: #52c41a;
              font-weight: 600;
            }
          }
        }

        .cost-savings {
          text-align: center;
          padding: 12px;
          background: rgba(82, 196, 26, 0.1);
          border-radius: 8px;
          color: #52c41a;
          font-weight: 600;
        }
      }
    }
  }
}

.cta-section {
  padding: 100px 20px;
  background: linear-gradient(135deg, #000, #000);
  text-align: center;

  .cta-container {
    max-width: 800px;
    margin: 0 auto;

    h2 {
      font-size: 36px;
      font-weight: 700;
      color: white;
      margin: 0 0 16px;
    }

    p {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.9);
      margin: 0 0 40px;
    }

    .cta-actions {
      display: flex;
      justify-content: center;
      gap: 20px;

      .el-button {
        height: 50px;
        padding: 0 32px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 25px;

        &.el-button--primary {
          background: white;
          color: #667eea;
          border: none;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(255, 255, 255, 0.3);
          }
        }

        &:not(.el-button--primary) {
          background: transparent;
          border: 2px solid rgba(255, 255, 255, 0.5);
          color: white;

          &:hover {
            background: rgba(255, 255, 255, 0.1);
            border-color: white;
          }
        }
      }
    }
  }
}

.main-footer {
  background: #0a0e1a;
  padding: 60px 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .footer-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .footer-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 40px;
    margin-bottom: 40px;
  }

  .footer-section {
    h4 {
      font-size: 18px;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 20px;
    }

    p {
      color: #8892b0;
      line-height: 1.6;
      margin: 0 0 8px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        margin-bottom: 8px;

        a {
          color: #8892b0;
          text-decoration: none;
          transition: color 0.3s ease;

          &:hover {
            color: #667eea;
          }
        }
      }
    }
  }

  .footer-bottom {
    text-align: center;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    p {
      color: #8892b0;
      margin: 0;
    }
  }
}

@media (max-width: 768px) {
  .gpu-grid {
    grid-template-columns: 1fr !important;
  }

  .cost-grid {
    grid-template-columns: 1fr !important;
  }

  .cases-grid {
    grid-template-columns: 1fr !important;
  }

  .header-nav {
    display: none !important;
  }
}
</style>

